import axios from 'axios';
import axiosInstance from '@/lib/axios';
import { prepareImageFile } from '@/lib/prepare-image-file';

type VertexUploadPath = '/api/upload' | '/api/upload/resource';

function getVertexUrl(): string {
  const vertexUrl = import.meta.env.VITE_VERTEX_BASE_URL;
  if (!vertexUrl) {
    throw new Error('VITE_VERTEX_BASE_URL is required');
  }
  return vertexUrl;
}

function vertexUploadHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function postToVertexUpload(
  path: VertexUploadPath,
  formData: FormData
): Promise<{ url: string }> {
  try {
    const { data } = await axiosInstance.post<{ url?: string; error?: string }>(
      `${getVertexUrl()}${path}`,
      formData,
      {
        withCredentials: false,
        headers: vertexUploadHeaders(),
      }
    );

    const url = data?.url;
    if (typeof url !== 'string' || url.trim().length === 0) {
      throw new Error(
        typeof data?.error === 'string' && data.error.length > 0
          ? data.error
          : 'Upload failed: no URL returned'
      );
    }
    return { url };
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const payload = err.response?.data as { error?: string } | undefined;
      const message =
        typeof payload?.error === 'string' && payload.error.length > 0
          ? payload.error
          : `Upload failed (${err.response?.status ?? '?'})`;
      throw new Error(message);
    }
    throw err;
  }
}

async function withPreparedImageFile(formData: FormData): Promise<FormData> {
  const file = formData.get('file');
  if (!(file instanceof File) || file.size === 0) return formData;

  const prepared = await prepareImageFile(file);
  if (prepared === file) return formData;

  const next = new FormData();
  for (const [key, value] of formData.entries()) {
    if (key === 'file') {
      next.append('file', prepared, prepared.name);
    } else if (key === 'filename') {
      next.append('filename', prepared.name);
    } else {
      next.append(key, value);
    }
  }
  return next;
}

export async function postMultipartImageUpload(formData: FormData): Promise<{ url: string }> {
  return postToVertexUpload('/api/upload', await withPreparedImageFile(formData));
}

export async function postMultipartResourceUpload(formData: FormData): Promise<{ url: string }> {
  return postToVertexUpload('/api/upload/resource', formData);
}

/** Images use `/api/upload`; PDFs and other documents use `/api/upload/resource`. */
export async function postMultipartDocumentUpload(
  formData: FormData,
  file: File
): Promise<{ url: string }> {
  const payload = await withPreparedImageFile(formData);
  const uploaded = payload.get('file');
  const prepared = uploaded instanceof File ? uploaded : file;
  if (!prepared.type.startsWith('image/')) {
    return postMultipartResourceUpload(payload);
  }
  return postToVertexUpload('/api/upload', payload);
}

export async function uploadFilesToVertex(files: File[], folder: string): Promise<string[]> {
  const valid = files.filter((file) => file instanceof File && file.size > 0);
  return Promise.all(
    valid.map(async (file) => {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('filename', file.name);
      formData.append('folder', folder);
      const { url } = await postMultipartDocumentUpload(formData, file);
      return url;
    })
  );
}
